import { type MouseEvent as ReactMouseEvent } from "react";

import { Column, Row, Text } from "@/components";
import { GoalPanelPeriodToggle } from "@/components/dashboard/dashboard-stats/main-stats/GoalPanelPeriodToggle";
import { TtfsGoalDelta } from "@/components/dashboard/dashboard-stats/main-stats/TtfsGoalDelta";

import {
  GOAL_PANEL_CONTAINER_CLASS,
  GOAL_PANEL_NARROW_LABEL_CLASS,
  GOAL_PANEL_NARROW_STACK_CLASS,
  GOAL_PANEL_VALUE_CLASS,
  SCREEN_HOURS_GREEN_MAX,
} from "@/lib/constants";
import { cn } from "@/lib/utils";
import { GoalPeriod, PerformanceTier } from "@/types";

// Medians come back in tenths of an hour, so subtracting the goal off one
// leaves float noise (26.9 - 12 = 14.899999999999999) to round away.
const TENTHS_PER_UNIT = 10;

// Selected, the panel wears its own grade: under 12h green, under 24h orange,
// past that red. Literal classes so Tailwind emits them.
const SELECTED_PANEL_CLASS: Record<PerformanceTier, string> = {
  [PerformanceTier.GREEN]:
    "border-[#30A46C] bg-[#30A46C] shadow-md dark:border-[#39753B] dark:bg-[#315F34]",
  [PerformanceTier.ORANGE]:
    "border-[#EE9D2B] bg-[#EE9D2B] shadow-md dark:border-[#F39562] dark:bg-[#8D4721]",
  [PerformanceTier.RED]:
    "border-[#E5484D] bg-[#E5484D] shadow-md dark:border-[#783129] dark:bg-[#602A24]",
};

export const TtfsGoalPanel = ({
  goalsPeriod,
  isIndividual,
  isSelected,
  medianHours,
  onGoalsPeriodChange,
  onOpenScreenBreakdown,
  tier,
}: {
  goalsPeriod: GoalPeriod;
  isIndividual: boolean;
  isSelected: boolean;
  medianHours: number | null;
  onGoalsPeriodChange: (period: GoalPeriod) => void;
  // Individual view only — the breakdown is per staffer.
  onOpenScreenBreakdown?: () => void;
  tier: PerformanceTier;
}) => {
  /* Helpers */
  const textClass = isSelected ? "text-white" : "text-slate-900";
  const hoursOffGoal =
    medianHours === null
      ? null
      : Math.round((medianHours - SCREEN_HOURS_GREEN_MAX) * TENTHS_PER_UNIT) /
        TENTHS_PER_UNIT;

  /* Handlers */
  // The panel behind it selects the metric, so the drill-down keeps the click.
  const handleMedianClick = (event: ReactMouseEvent) => {
    if (!onOpenScreenBreakdown) return;
    event.stopPropagation();
    onOpenScreenBreakdown();
  };

  /* Render */
  const render = () => (
    <Column
      className={cn(
        GOAL_PANEL_CONTAINER_CLASS,
        "relative h-full w-full justify-between rounded-xl border p-4 transition-colors",
        isSelected ? SELECTED_PANEL_CLASS[tier] : "border-slate-200 bg-white",
      )}
    >
      <Row className="items-start justify-between gap-2">
        <Text
          className={cn(
            "font-plex-mono text-[9px] font-semibold uppercase tracking-wider",
            textClass,
          )}
        >
          {isIndividual ? "Time to first screen" : "Team TTFS Median"}
        </Text>
      </Row>
      {isSelected ? (
        <GoalPanelPeriodToggle
          onPeriodChange={onGoalsPeriodChange}
          period={goalsPeriod}
        />
      ) : null}
      <Row className={cn("items-end gap-2", GOAL_PANEL_NARROW_STACK_CLASS)}>
        <Column
          className={cn(
            "w-fit",
            onOpenScreenBreakdown && "cursor-pointer hover:opacity-80",
          )}
          onClick={handleMedianClick}
        >
          <Text
            className={cn(
              GOAL_PANEL_VALUE_CLASS,
              "whitespace-nowrap font-semibold tabular-nums leading-none tracking-tight",
              textClass,
            )}
          >
            {medianHours === null ? "—" : `${medianHours.toString()}h`}
          </Text>
        </Column>
        <Text
          className={cn(
            GOAL_PANEL_NARROW_LABEL_CLASS,
            "pb-0.5 text-[10px] font-medium opacity-80",
            textClass,
          )}
        >
          / {SCREEN_HOURS_GREEN_MAX.toString()}h
        </Text>
      </Row>
      <Row className="items-center justify-between gap-2">
        <Row
          className={cn(
            "min-w-0 flex-1 items-center gap-1 rounded-md px-2 py-1.5",
            isSelected ? "bg-white/15" : "bg-slate-100",
          )}
        >
          <TtfsGoalDelta hoursOffGoal={hoursOffGoal} textClass={textClass} />
        </Row>
      </Row>
    </Column>
  );

  return render();
};
